import type { BacklogConfig } from "./contracts/BacklogConfig";
import type { BacklogApiContext } from "./contracts/api/BacklogApiContext";
import type { BacklogResponse } from "./contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "./contracts/api/BacklogResponseFile";
import type { BacklogResponseNoContent } from "./contracts/api/BacklogResponseNoContent";
import { getActivity } from "./endpoints/BacklogActivityApi";
import {
	addGroup,
	addProjectGroup,
	deleteGroup,
	deleteProjectGroup,
	getGroup,
	getGroupIcon,
	getListOfGroups,
	getProjectGroupList,
	updateGroup,
} from "./endpoints/BacklogGroupApi";
import {
	addIssue,
	addIssueComment,
	addIssuecommentNotification,
	countIssue,
	countIssueComment,
	deleteIssue,
	deleteIssueAttachment,
	deleteIssueComment,
	getIssue,
	getIssueAttachment,
	getIssueComment,
	getIssueCommentList,
	getIssueList,
	getIssueParticipantList,
	getListOfIssueAttachments,
	getListOfIssueCommentNotifications,
	getListOfIssueLinkedSharedFiles,
	linkSharedFilesToIssue,
	removeLinkToSharedFileFromIssue,
	updateIssue,
	updateIssueComment,
} from "./endpoints/BacklogIssueApi";
import { getLicence } from "./endpoints/BacklogLicenseApi";
import {
	countNotification,
	getNotification,
	readNotification,
	resetUnreadNotificationCount,
} from "./endpoints/BacklogNotificationApi";
import {
	addCategory,
	addCustomField,
	addIssueType,
	addListItemForListTypeCustomField,
	addProject,
	addProjectAdministrator,
	addProjectUser,
	addStatus,
	addVersionMilestone,
	deleteCategory,
	deleteCustomField,
	deleteIssueType,
	deleteListItemForListTypeCustomField,
	deleteProject,
	deleteProjectAdministrator,
	deleteProjectUser,
	deleteStatus,
	deleteVersion,
	getCategoryList,
	getCustomFieldList,
	getFile,
	getIssueTypeList,
	getListOfProjectAdministrators,
	getListOfSharedFiles,
	getPriorityList,
	getProject,
	getProjectDiskUsage,
	getProjectIcon,
	getProjectList,
	getProjectRecentUpdates,
	getResolutionList,
	getStatusList,
	getStatusListOfProject,
	getVersionMilestoneList,
	updateCategory,
	updateCustomField,
	updateIssueType,
	updateListItemForListTypeCustomField,
	updateOrderOfStatus,
	updateProject,
	updateStatus,
	updateVersionMilestone,
} from "./endpoints/BacklogProjectApi";
import { getProjectUserList } from "./endpoints/BacklogProjectApi";
import { getRateLimit } from "./endpoints/BacklogRateLimitApi";
import {
	addPullRequest,
	addPullRequestComment,
	deletePullRequestAttachments,
	downloadPullRequestAttachment,
	getGitRepository,
	getListOfGitRepositories,
	getListOfPullRequestAttachment,
	getNumberOfPullRequestComments,
	getNumberOfPullRequests,
	getPullRequest,
	getPullRequestComment,
	getPullRequestList,
	updatePullRequest,
	updatePullRequestCommentInformation,
} from "./endpoints/BacklogRepositoryApi";
import {
	getRecentUpdates,
	getSpace,
	getSpaceDiskUsage,
	getSpaceLogo,
	getSpaceNotification,
	postAttachmentFile,
	updateSpaceNotification,
} from "./endpoints/BacklogSpaceApi";
import { addStar } from "./endpoints/BacklogStarApi";
import {
	addProjectTeam,
	addTeam,
	deleteProjectTeam,
	deleteTeam,
	getListOfTeams,
	getProjectTeamList,
	getTeam,
	getTeamIcon,
	updateTeam,
} from "./endpoints/BacklogTeamApi";
import {
	addRecentlyViewedIssue,
	addRecentlyViewedWiki,
	addUser,
	countUserReceivedStars,
	deleteUser,
	getListOfRecentlyViewedIssues,
	getListOfRecentlyViewedProjects,
	getListOfRecentlyViewedWikis,
	getOwnUser,
	getReceivedStarList,
	getUser,
	getUserList,
	getUserRecentUpdates,
	updateUser,
} from "./endpoints/BacklogUserApi";
import { getUserIcon } from "./endpoints/BacklogUserApi";
import {
	addWatching,
	countWatching,
	deleteWatching,
	getWatching,
	getWatchingList,
	markWatchingAsRead,
	updateWatching,
} from "./endpoints/BacklogWatchingApi";
import {
	addWebhook,
	deleteWebhook,
	getListOfWebhooks,
	getWebhook,
	updateWebhook,
} from "./endpoints/BacklogWebhookApi";
import {
	addWikiPage,
	attachFileToWiki,
	countWikiPage,
	deleteWikiPage,
	getListOfSharedFilesOnWiki,
	getListOfWikiAttachments,
	getWikiPage,
	getWikiPageAttachment,
	getWikiPageHistory,
	getWikiPageList,
	getWikiPageStar,
	getWikiPageTagList,
	linkSharedFilesToWiki,
	removeLinkToSharedFileFromWiki,
	removeWikiAttachment,
	updateWikiPage,
} from "./endpoints/BacklogWikiApi";
import type { AddCategoryParams } from "./endpoints/params/AddCategoryParams";
import type { AddIssueCommentParams } from "./endpoints/params/AddIssueCommentParams";
import type { AddIssueParams } from "./endpoints/params/AddIssueParams";
import type { AddIssueTypeParams } from "./endpoints/params/AddIssueTypeParams";
import type { AddIssuecommentNotificationParams } from "./endpoints/params/AddIssuecommentNotificationParams";
import type { AddProjectAdministratorParams } from "./endpoints/params/AddProjectAdministratorParams";
import type { AddProjectParams } from "./endpoints/params/AddProjectParams";
import type { AddProjectTeamParams } from "./endpoints/params/AddProjectTeamParams";
import type { AddProjectUserParams } from "./endpoints/params/AddProjectUserParams";
import type { AddPullRequestCommentParams } from "./endpoints/params/AddPullRequestCommentParams";
import type { AddPullRequestParams } from "./endpoints/params/AddPullRequestParams";
import type { AddRecentlyViewedIssueParams } from "./endpoints/params/AddRecentlyViewedIssueParams";
import type { AddRecentlyViewedWikiParams } from "./endpoints/params/AddRecentlyViewedWikiParams";
import type { AddStarParams } from "./endpoints/params/AddStarParams";
import type { AddStatusParams } from "./endpoints/params/AddStatusParams";
import type { AddVersionMilestoneParams } from "./endpoints/params/AddVersionMilestoneParams";
import type { AddWatchingParams } from "./endpoints/params/AddWatchingParams";
import type { AddWebhookParams } from "./endpoints/params/AddWebhookParams";
import type { AddWikiPageParams } from "./endpoints/params/AddWikiPageParams";
import type { AttachFileToWikiParams } from "./endpoints/params/AttachFileToWikiParams";
import type { CountIssueCommentParams } from "./endpoints/params/CountIssueCommentParams";
import type { CountIssueParams } from "./endpoints/params/CountIssueParams";
import type { CountNotificationParams } from "./endpoints/params/CountNotificationParams";
import type { CountUserReceivedStarsParams } from "./endpoints/params/CountUserReceivedStarsParams";
import type { CountWatchingParams } from "./endpoints/params/CountWatchingParams";
import type { CountWikiPageParams } from "./endpoints/params/CountWikiPageParams";
import type { DeleteCategoryParams } from "./endpoints/params/DeleteCategoryParams";
import type { DeleteIssueAttachmentParams } from "./endpoints/params/DeleteIssueAttachmentParams";
import type { DeleteIssueCommentParams } from "./endpoints/params/DeleteIssueCommentParams";
import type { DeleteIssueParams } from "./endpoints/params/DeleteIssueParams";
import type { DeleteIssueTypeParams } from "./endpoints/params/DeleteIssueTypeParams";
import type { DeleteProjectAdministratorParams } from "./endpoints/params/DeleteProjectAdministratorParams";
import type { DeleteProjectParams } from "./endpoints/params/DeleteProjectParams";
import type { DeleteProjectTeamParams } from "./endpoints/params/DeleteProjectTeamParams";
import type { DeleteProjectUserParams } from "./endpoints/params/DeleteProjectUserParams";
import type { DeletePullRequestAttachmentsParams } from "./endpoints/params/DeletePullRequestAttachmentsParams";
import type { DeleteStatusParams } from "./endpoints/params/DeleteStatusParams";
import type { DeleteVersionParams } from "./endpoints/params/DeleteVersionParams";
import type { DeleteWatchingParams } from "./endpoints/params/DeleteWatchingParams";
import type { DeleteWebhookParams } from "./endpoints/params/DeleteWebhookParams";
import type { DeleteWikiPageParams } from "./endpoints/params/DeleteWikiPageParams";
import type { DownloadPullRequestAttachmentParams } from "./endpoints/params/DownloadPullRequestAttachmentParams";
import type { GetActivityParams } from "./endpoints/params/GetActivityParams";
import type { GetCategoryListParams } from "./endpoints/params/GetCategoryListParams";
import type { GetIssueCommentListParams } from "./endpoints/params/GetCommentListParams";
import type { GetFileParams } from "./endpoints/params/GetFileParams";
import type { GetGitRepositoryParams } from "./endpoints/params/GetGitRepositoryParams";
import type { GetIssueAttachmentParams } from "./endpoints/params/GetIssueAttachmentParams";
import type { GetIssueCommentParams } from "./endpoints/params/GetIssueCommentParams";
import type { GetIssueListParams } from "./endpoints/params/GetIssueListParams";
import type { GetIssueParams } from "./endpoints/params/GetIssueParams";
import type { GetIssueParticipantListParams } from "./endpoints/params/GetIssueParticipantListParams";
import type { GetIssueTypeListParams } from "./endpoints/params/GetIssueTypeListParams";
import type { GetListOfGitRepositoriesParams } from "./endpoints/params/GetListOfGitRepositoriesParams";
import type { GetListOfIssueAttachmentsParams } from "./endpoints/params/GetListOfIssueAttachmentsParams";
import type { GetListOfIssueCommentNotificationsParams } from "./endpoints/params/GetListOfIssueCommentNotificationsParams";
import type { GetListOfIssueLinkedSharedFilesParams } from "./endpoints/params/GetListOfIssueLinkedSharedFilesParams";
import type { GetListOfProjectAdministratorsParams } from "./endpoints/params/GetListOfProjectAdministratorsParams";
import type { GetListOfPullRequestAttachmentParams } from "./endpoints/params/GetListOfPullRequestAttachmentParams";
import type { GetListOfRecentlyViewedIssuesParams } from "./endpoints/params/GetListOfRecentlyViewedIssuesParams";
import type { GetListOfRecentlyViewedProjectsParams } from "./endpoints/params/GetListOfRecentlyViewedProjectsParams";
import type { GetListOfRecentlyViewedWikisParams } from "./endpoints/params/GetListOfRecentlyViewedWikisParams";
import type { GetListOfSharedFilesOnWikiParams } from "./endpoints/params/GetListOfSharedFilesOnWikiParams";
import type { GetListOfSharedFilesParams } from "./endpoints/params/GetListOfSharedFilesParams";
import type { GetListOfTeamsParams } from "./endpoints/params/GetListOfTeamsParams";
import type { GetListOfWebhooksParams } from "./endpoints/params/GetListOfWebhooksParams";
import type { GetListOfWikiAttachmentsParams } from "./endpoints/params/GetListOfWikiAttachmentsParams";
import type { GetNotificationParams } from "./endpoints/params/GetNotificationParams";
import type { GetNumberOfPullRequestCommentsParams } from "./endpoints/params/GetNumberOfPullRequestCommentsParams";
import type { GetNumberOfPullRequestsParams } from "./endpoints/params/GetNumberOfPullRequestsParams";
import type { GetProjectDiskUsageParams } from "./endpoints/params/GetProjectDiskUsageParams";
import type { GetProjectIconParams } from "./endpoints/params/GetProjectIconParams";
import type { GetProjectListParams } from "./endpoints/params/GetProjectListParams";
import type { GetProjectParams } from "./endpoints/params/GetProjectParams";
import type { GetProjectRecentUpdatesParams } from "./endpoints/params/GetProjectRecentUpdatesParams";
import type { GetProjectTeamListParams } from "./endpoints/params/GetProjectTeamListParams";
import type { GetProjectUserListParams } from "./endpoints/params/GetProjectUserListParams";
import type { GetPullRequestCommentParams } from "./endpoints/params/GetPullRequestCommentParams";
import type { GetPullRequestListParams } from "./endpoints/params/GetPullRequestListParams";
import type { GetPullRequestParams } from "./endpoints/params/GetPullRequestParams";
import type { GetReceivedStarListParams } from "./endpoints/params/GetReceivedStarListParams";
import type { GetRecentUpdatesParams } from "./endpoints/params/GetRecentUpdatesParams";
import type { GetStatusListOfProjectParams } from "./endpoints/params/GetStatusListOfProjectParams";
import type { GetTeamIconParams } from "./endpoints/params/GetTeamIconParams";
import type { GetTeamParams } from "./endpoints/params/GetTeamParams";
import type { GetUserIconParams } from "./endpoints/params/GetUserIconParams";
import type { GetUserParams } from "./endpoints/params/GetUserParams";
import type { GetUserRecentUpdatesParams } from "./endpoints/params/GetUserRecentUpdatesParams";
import type { GetVersionMilestoneListParams } from "./endpoints/params/GetVersionMilestoneListParams";
import type { GetWatchingListParams } from "./endpoints/params/GetWatchingListParams";
import type { GetWatchingParams } from "./endpoints/params/GetWatchingParams";
import type { GetWebhookParams } from "./endpoints/params/GetWebhookParams";
import type { GetWikiPageAttachmentParams } from "./endpoints/params/GetWikiPageAttachmentParams";
import type { GetWikiPageHistoryParams } from "./endpoints/params/GetWikiPageHistoryParams";
import type { GetWikiPageListParams } from "./endpoints/params/GetWikiPageListParams";
import type { GetWikiPageParams } from "./endpoints/params/GetWikiPageParams";
import type { GetWikiPageStarParams } from "./endpoints/params/GetWikiPageStarParams";
import type { GetWikiPageTagListParams } from "./endpoints/params/GetWikiPageTagListParams";
import type { LinkSharedFilesToIssueParams } from "./endpoints/params/LinkSharedFilesToIssueParams";
import type { LinkSharedFilesToWikiParams } from "./endpoints/params/LinkSharedFilesToWikiParams";
import type { MarkWatchingAsReadParams } from "./endpoints/params/MarkWatchingAsReadParams";
import type { PostAttachmentFileParams } from "./endpoints/params/PostAttachmentFileParams";
import type { ReadNotificationParams } from "./endpoints/params/ReadNotificationParams";
import type { RemoveLinkToSharedFileFromIssueParams } from "./endpoints/params/RemoveLinkToSharedFileFromIssueParams";
import type { RemoveLinkToSharedFileFromWikiParams } from "./endpoints/params/RemoveLinkToSharedFileFromWikiParams";
import type { RemoveWikiAttachmentParams } from "./endpoints/params/RemoveWikiAttachmentParams";
import type { UpdateCategoryParams } from "./endpoints/params/UpdateCategoryParams";
import type { UpdateIssueCommentParams } from "./endpoints/params/UpdateIssueCommentParams";
import type { UpdateIssueParams } from "./endpoints/params/UpdateIssueParams";
import type { UpdateIssueTypeParams } from "./endpoints/params/UpdateIssueTypeParams";
import type { UpdateOrderOfStatusParams } from "./endpoints/params/UpdateOrderOfStatusParams";
import type { UpdateProjectParams } from "./endpoints/params/UpdateProjectParams";
import type { UpdatePullRequestCommentInformationParams } from "./endpoints/params/UpdatePullRequestCommentInformationParams";
import type { UpdatePullRequestParams } from "./endpoints/params/UpdatePullRequestParams";
import type { UpdateSpaceNotificationParams } from "./endpoints/params/UpdateSpaceNotificationParams";
import type { UpdateStatusParams } from "./endpoints/params/UpdateStatusParams";
import type { UpdateVersionMilestoneParams } from "./endpoints/params/UpdateVersionMilestoneParams";
import type { UpdateWatchingParams } from "./endpoints/params/UpdateWatchingParams";
import type { UpdateWebhookParams } from "./endpoints/params/UpdateWebhookParams";
import type { UpdateWikiPageParams } from "./endpoints/params/UpdateWikiPageParams";
import type { Activity } from "./entities/Activity";
import type { AttachmentFile } from "./entities/AttachmentFile";
import type { Category } from "./entities/Category";
import type { Issue } from "./entities/Issue";
import type { IssueAttachmentFile } from "./entities/IssueAttachmentFile";
import type { IssueComment } from "./entities/IssueComment";
import type { IssueCommentCount } from "./entities/IssueCommentCount";
import type { IssueCommentNotification } from "./entities/IssueCommentNotification";
import type { IssueCount } from "./entities/IssueCount";
import type { IssueStatus } from "./entities/IssueStatus";
import type { IssueTypeDetailed } from "./entities/IssueTypeDetailed";
import type { Licence } from "./entities/Licence";
import type { NotificationCount } from "./entities/NotificationCount";
import type { NotificationUnreadCount } from "./entities/NotificationUnreadCount";
import type { Priority } from "./entities/Priority";
import type { Project } from "./entities/Project";
import type { ProjectActivity } from "./entities/ProjectActivity";
import type { ProjectDiskUsage } from "./entities/ProjectDiskUsage";
import type { ProjectStatus } from "./entities/ProjectStatus";
import type { PullRequest } from "./entities/PullRequest";
import type { PullRequestAttachmentFile } from "./entities/PullRequestAttachmentFile";
import type { PullRequestComment } from "./entities/PullRequestComment";
import type { PullRequestCommentCount } from "./entities/PullRequestCommentCount";
import type { PullRequestCount } from "./entities/PullRequestCount";
import type { RateLimit } from "./entities/RateLimit";
import type { RecentlyViewedIssue } from "./entities/RecentlyViewedIssue";
import type { RecentlyViewedProject } from "./entities/RecentlyViewedProject";
import type { RecentlyViewedWiki } from "./entities/RecentlyViewedWiki";
import type { Repository } from "./entities/Repository";
import type { Resolution } from "./entities/Resolution";
import type { SharedFile } from "./entities/SharedFile";
import type { Space } from "./entities/Space";
import type { SpaceActivity } from "./entities/SpaceActivity";
import type { SpaceDiskUsage } from "./entities/SpaceDiskUsage";
import type { SpaceNotification } from "./entities/SpaceNotification";
import type { Star } from "./entities/Star";
import type { StarCount } from "./entities/StarCount";
import type { Team } from "./entities/Team";
import type { User } from "./entities/User";
import type { UserActivity } from "./entities/UserActivity";
import type { UserNotification } from "./entities/UserNotification";
import type { Version } from "./entities/Version";
import type { WatchingItem } from "./entities/WatchingItem";
import type { WatchingItemCount } from "./entities/WatchingItemCount";
import type { Webhook } from "./entities/Webhook";
import type { Wiki } from "./entities/Wiki";
import type { WikiAttachmentFile } from "./entities/WikiAttachmentFile";
import type { WikiCount } from "./entities/WikiCount";
import type { WikiHistory } from "./entities/WikiHistory";
import type { WikiTag } from "./entities/WikiTag";
import type { EmptyObject } from "./types/EmptyObject";
import { loadBacklogMetadata } from "./utils/BacklogUtil";
import type { TailType } from "./utils/TailType";

export class BacklogApiClient {
	private readonly config: BacklogConfig;
	constructor(options: { config: BacklogConfig }) {
		this.config = options.config;
		loadBacklogMetadata();
	}

	getSpace(context?: BacklogApiContext): Promise<BacklogResponse<Space>> {
		return this.currying(getSpace)(context);
	}

	getRecentUpdates(
		params?: GetRecentUpdatesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SpaceActivity[]>> {
		return this.currying(getRecentUpdates)(params, context);
	}

	getActivity(
		params: GetActivityParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Activity>> {
		return this.currying(getActivity)(params, context);
	}

	getSpaceLogo(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getSpaceLogo)(context);
	}

	getSpaceNotification(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SpaceNotification>> {
		return this.currying(getSpaceNotification)(context);
	}

	updateSpaceNotification(
		params: UpdateSpaceNotificationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SpaceNotification>> {
		return this.currying(updateSpaceNotification)(params, context);
	}

	getSpaceDiskUsage(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SpaceDiskUsage>> {
		return this.currying(getSpaceDiskUsage)(context);
	}

	postAttachmentFile(
		params: PostAttachmentFileParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<AttachmentFile>> {
		return this.currying(postAttachmentFile)(params, context);
	}

	getUserList(context?: BacklogApiContext): Promise<BacklogResponse<User[]>> {
		return this.currying(getUserList)(context);
	}

	getUser(
		params: GetUserParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User>> {
		return this.currying(getUser)(params, context);
	}

	addUser(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addUser)(params, context);
	}

	updateUser(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(updateUser)(params, context);
	}

	deleteUser(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteUser)(params, context);
	}

	getOwnUser(context?: BacklogApiContext): Promise<BacklogResponse<User>> {
		return this.currying(getOwnUser)(context);
	}

	getUserIcon(
		params: GetUserIconParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getUserIcon)(params, context);
	}

	getUserRecentUpdates(
		params: GetUserRecentUpdatesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<UserActivity[]>> {
		return this.currying(getUserRecentUpdates)(params, context);
	}

	getReceivedStarList(
		params: GetReceivedStarListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Star[]>> {
		return this.currying(getReceivedStarList)(params, context);
	}

	countUserReceivedStars(
		params: CountUserReceivedStarsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<StarCount>> {
		return this.currying(countUserReceivedStars)(params, context);
	}

	getListOfRecentlyViewedIssues(
		params?: GetListOfRecentlyViewedIssuesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<RecentlyViewedIssue[]>> {
		return this.currying(getListOfRecentlyViewedIssues)(params, context);
	}

	addRecentlyViewedIssue(
		params: AddRecentlyViewedIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue>> {
		return this.currying(addRecentlyViewedIssue)(params, context);
	}

	getListOfRecentlyViewedProjects(
		params?: GetListOfRecentlyViewedProjectsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<RecentlyViewedProject[]>> {
		return this.currying(getListOfRecentlyViewedProjects)(params, context);
	}

	getListOfRecentlyViewedWikis(
		params?: GetListOfRecentlyViewedWikisParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<RecentlyViewedWiki[]>> {
		return this.currying(getListOfRecentlyViewedWikis)(params, context);
	}

	addRecentlyViewedWiki(
		params: AddRecentlyViewedWikiParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<RecentlyViewedWiki>> {
		return this.currying(addRecentlyViewedWiki)(params, context);
	}

	/** @deprecated */
	getListOfGroups(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getListOfGroups)(params, context);
	}

	/** @deprecated */
	addGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addGroup)(params, context);
	}

	/** @deprecated */
	getGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getGroup)(params, context);
	}

	/** @deprecated */
	updateGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(updateGroup)(params, context);
	}

	/** @deprecated */
	deleteGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteGroup)(params, context);
	}

	/** @deprecated */
	getStatusList(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getStatusList)(params, context);
	}

	getStatusListOfProject(
		params: GetStatusListOfProjectParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<ProjectStatus[]>> {
		return this.currying(getStatusListOfProject)(params, context);
	}

	getPriorityList(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Priority[]>> {
		return this.currying(getPriorityList)(context);
	}

	getResolutionList(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Resolution[]>> {
		return this.currying(getResolutionList)(context);
	}

	getProjectList(
		params?: GetProjectListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Project[]>> {
		return this.currying(getProjectList)(params, context);
	}

	addProject(
		params: AddProjectParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Project>> {
		return this.currying(addProject)(params, context);
	}

	getProject(
		params: GetProjectParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Project>> {
		return this.currying(getProject)(params, context);
	}

	updateProject(
		params: UpdateProjectParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Project>> {
		return this.currying(updateProject)(params, context);
	}

	deleteProject(
		params: DeleteProjectParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Project>> {
		return this.currying(deleteProject)(params, context);
	}

	getProjectIcon(
		params: GetProjectIconParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getProjectIcon)(params, context);
	}

	getProjectRecentUpdates(
		params: GetProjectRecentUpdatesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<ProjectActivity[]>> {
		return this.currying(getProjectRecentUpdates)(params, context);
	}

	addProjectUser(
		params: AddProjectUserParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User>> {
		return this.currying(addProjectUser)(params, context);
	}

	getProjectUserList(
		params: GetProjectUserListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User[]>> {
		return this.currying(getProjectUserList)(params, context);
	}

	deleteProjectUser(
		params: DeleteProjectUserParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User>> {
		return this.currying(deleteProjectUser)(params, context);
	}

	addProjectAdministrator(
		params: AddProjectAdministratorParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User>> {
		return this.currying(addProjectAdministrator)(params, context);
	}

	getListOfProjectAdministrators(
		params: GetListOfProjectAdministratorsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User[]>> {
		return this.currying(getListOfProjectAdministrators)(params, context);
	}

	deleteProjectAdministrator(
		params: DeleteProjectAdministratorParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User>> {
		return this.currying(deleteProjectAdministrator)(params, context);
	}

	addStatus(
		params: AddStatusParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueStatus>> {
		return this.currying(addStatus)(params, context);
	}

	updateStatus(
		params: UpdateStatusParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueStatus>> {
		return this.currying(updateStatus)(params, context);
	}

	deleteStatus(
		params: DeleteStatusParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueStatus>> {
		return this.currying(deleteStatus)(params, context);
	}

	updateOrderOfStatus(
		params: UpdateOrderOfStatusParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueStatus[]>> {
		return this.currying(updateOrderOfStatus)(params, context);
	}

	getIssueTypeList(
		params: GetIssueTypeListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueTypeDetailed[]>> {
		return this.currying(getIssueTypeList)(params, context);
	}

	addIssueType(
		params: AddIssueTypeParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueTypeDetailed>> {
		return this.currying(addIssueType)(params, context);
	}

	updateIssueType(
		params: UpdateIssueTypeParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueTypeDetailed>> {
		return this.currying(updateIssueType)(params, context);
	}

	deleteIssueType(
		params: DeleteIssueTypeParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueTypeDetailed>> {
		return this.currying(deleteIssueType)(params, context);
	}

	getCategoryList(
		params: GetCategoryListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Category[]>> {
		return this.currying(getCategoryList)(params, context);
	}

	addCategory(
		params: AddCategoryParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Category>> {
		return this.currying(addCategory)(params, context);
	}

	updateCategory(
		params: UpdateCategoryParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Category>> {
		return this.currying(updateCategory)(params, context);
	}

	deleteCategory(
		params: DeleteCategoryParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Category>> {
		return this.currying(deleteCategory)(params, context);
	}

	getVersionMilestoneList(
		params: GetVersionMilestoneListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Version[]>> {
		return this.currying(getVersionMilestoneList)(params, context);
	}

	addVersionMilestone(
		params: AddVersionMilestoneParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Version>> {
		return this.currying(addVersionMilestone)(params, context);
	}

	updateVersionMilestone(
		params: UpdateVersionMilestoneParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Version>> {
		return this.currying(updateVersionMilestone)(params, context);
	}

	deleteVersion(
		params: DeleteVersionParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Version>> {
		return this.currying(deleteVersion)(params, context);
	}

	getCustomFieldList(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getCustomFieldList)(params, context);
	}

	addCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addCustomField)(params, context);
	}

	updateCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(updateCustomField)(params, context);
	}

	deleteCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteCustomField)(params, context);
	}

	addListItemForListTypeCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addListItemForListTypeCustomField)(params, context);
	}

	updateListItemForListTypeCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(updateListItemForListTypeCustomField)(params, context);
	}

	deleteListItemForListTypeCustomField(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteListItemForListTypeCustomField)(params, context);
	}

	getListOfSharedFiles(
		params: GetListOfSharedFilesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile[]>> {
		return this.currying(getListOfSharedFiles)(params, context);
	}

	getFile(
		params: GetFileParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getFile)(params, context);
	}

	getProjectDiskUsage(
		params: GetProjectDiskUsageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<ProjectDiskUsage>> {
		return this.currying(getProjectDiskUsage)(params, context);
	}

	getListOfWebhooks(
		params: GetListOfWebhooksParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Webhook[]>> {
		return this.currying(getListOfWebhooks)(params, context);
	}

	addWebhook(
		params: AddWebhookParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Webhook>> {
		return this.currying(addWebhook)(params, context);
	}

	getWebhook(
		params: GetWebhookParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Webhook>> {
		return this.currying(getWebhook)(params, context);
	}

	updateWebhook(
		params: UpdateWebhookParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Webhook>> {
		return this.currying(updateWebhook)(params, context);
	}

	deleteWebhook(
		params: DeleteWebhookParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Webhook>> {
		return this.currying(deleteWebhook)(params, context);
	}

	getIssueList(
		params?: GetIssueListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue[]>> {
		return this.currying(getIssueList)(params, context);
	}

	countIssue(
		params?: CountIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueCount>> {
		return this.currying(countIssue)(params, context);
	}

	addIssue(
		params: AddIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue>> {
		return this.currying(addIssue)(params, context);
	}

	getIssue(
		params: GetIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue>> {
		return this.currying(getIssue)(params, context);
	}

	updateIssue(
		params: UpdateIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue>> {
		return this.currying(updateIssue)(params, context);
	}

	deleteIssue(
		params: DeleteIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Issue>> {
		return this.currying(deleteIssue)(params, context);
	}

	getIssueCommentList(
		params: GetIssueCommentListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment[]>> {
		return this.currying(getIssueCommentList)(params, context);
	}

	addIssueComment(
		params: AddIssueCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment>> {
		return this.currying(addIssueComment)(params, context);
	}

	countIssueComment(
		params: CountIssueCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueCommentCount>> {
		return this.currying(countIssueComment)(params, context);
	}

	getIssueComment(
		params: GetIssueCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment>> {
		return this.currying(getIssueComment)(params, context);
	}

	deleteIssueComment(
		params: DeleteIssueCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment>> {
		return this.currying(deleteIssueComment)(params, context);
	}

	updateIssueComment(
		params: UpdateIssueCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment>> {
		return this.currying(updateIssueComment)(params, context);
	}

	getListOfIssueCommentNotifications(
		params: GetListOfIssueCommentNotificationsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueCommentNotification[]>> {
		return this.currying(getListOfIssueCommentNotifications)(params, context);
	}

	addIssuecommentNotification(
		params: AddIssuecommentNotificationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueComment>> {
		return this.currying(addIssuecommentNotification)(params, context);
	}

	getListOfIssueAttachments(
		params: GetListOfIssueAttachmentsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueAttachmentFile[]>> {
		return this.currying(getListOfIssueAttachments)(params, context);
	}

	getIssueAttachment(
		params: GetIssueAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getIssueAttachment)(params, context);
	}

	deleteIssueAttachment(
		params: DeleteIssueAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<IssueAttachmentFile>> {
		return this.currying(deleteIssueAttachment)(params, context);
	}

	getIssueParticipantList(
		params: GetIssueParticipantListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<User[]>> {
		return this.currying(getIssueParticipantList)(params, context);
	}

	getListOfIssueLinkedSharedFiles(
		params: GetListOfIssueLinkedSharedFilesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile[]>> {
		return this.currying(getListOfIssueLinkedSharedFiles)(params, context);
	}

	linkSharedFilesToIssue(
		params: LinkSharedFilesToIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile[]>> {
		return this.currying(linkSharedFilesToIssue)(params, context);
	}

	removeLinkToSharedFileFromIssue(
		params: RemoveLinkToSharedFileFromIssueParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile>> {
		return this.currying(removeLinkToSharedFileFromIssue)(params, context);
	}

	getWikiPageList(
		params: GetWikiPageListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Wiki[]>> {
		return this.currying(getWikiPageList)(params, context);
	}

	countWikiPage(
		params: CountWikiPageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiCount>> {
		return this.currying(countWikiPage)(params, context);
	}

	getWikiPageTagList(
		params: GetWikiPageTagListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiTag[]>> {
		return this.currying(getWikiPageTagList)(params, context);
	}

	addWikiPage(
		params: AddWikiPageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Wiki>> {
		return this.currying(addWikiPage)(params, context);
	}

	getWikiPage(
		params: GetWikiPageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Wiki>> {
		return this.currying(getWikiPage)(params, context);
	}

	updateWikiPage(
		params: UpdateWikiPageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Wiki>> {
		return this.currying(updateWikiPage)(params, context);
	}

	deleteWikiPage(
		params: DeleteWikiPageParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Wiki>> {
		return this.currying(deleteWikiPage)(params, context);
	}

	getListOfWikiAttachments(
		params: GetListOfWikiAttachmentsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiAttachmentFile[]>> {
		return this.currying(getListOfWikiAttachments)(params, context);
	}

	attachFileToWiki(
		params: AttachFileToWikiParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiAttachmentFile[]>> {
		return this.currying(attachFileToWiki)(params, context);
	}

	getWikiPageAttachment(
		params: GetWikiPageAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getWikiPageAttachment)(params, context);
	}

	removeWikiAttachment(
		params: RemoveWikiAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiAttachmentFile>> {
		return this.currying(removeWikiAttachment)(params, context);
	}

	getListOfSharedFilesOnWiki(
		params: GetListOfSharedFilesOnWikiParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile[]>> {
		return this.currying(getListOfSharedFilesOnWiki)(params, context);
	}

	linkSharedFilesToWiki(
		params: LinkSharedFilesToWikiParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile[]>> {
		return this.currying(linkSharedFilesToWiki)(params, context);
	}

	removeLinkToSharedFileFromWiki(
		params: RemoveLinkToSharedFileFromWikiParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<SharedFile>> {
		return this.currying(removeLinkToSharedFileFromWiki)(params, context);
	}

	getWikiPageHistory(
		params: GetWikiPageHistoryParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WikiHistory[]>> {
		return this.currying(getWikiPageHistory)(params, context);
	}

	getWikiPageStar(
		params: GetWikiPageStarParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Star[]>> {
		return this.currying(getWikiPageStar)(params, context);
	}

	addStar(
		params: AddStarParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseNoContent>> {
		return this.currying(addStar)(params, context);
	}

	getNotification(
		params?: GetNotificationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<UserNotification[]>> {
		return this.currying(getNotification)(params, context);
	}

	countNotification(
		params?: CountNotificationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<NotificationCount>> {
		return this.currying(countNotification)(params, context);
	}

	resetUnreadNotificationCount(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<NotificationUnreadCount>> {
		return this.currying(resetUnreadNotificationCount)(context);
	}

	readNotification(
		params: ReadNotificationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseNoContent>> {
		return this.currying(readNotification)(params, context);
	}

	getListOfGitRepositories(
		params: GetListOfGitRepositoriesParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Repository[]>> {
		return this.currying(getListOfGitRepositories)(params, context);
	}

	getGitRepository(
		params: GetGitRepositoryParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Repository>> {
		return this.currying(getGitRepository)(params, context);
	}

	getPullRequestList(
		params: GetPullRequestListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequest>> {
		return this.currying(getPullRequestList)(params, context);
	}

	getNumberOfPullRequests(
		params: GetNumberOfPullRequestsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestCount>> {
		return this.currying(getNumberOfPullRequests)(params, context);
	}

	addPullRequest(
		params: AddPullRequestParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequest>> {
		return this.currying(addPullRequest)(params, context);
	}

	getPullRequest(
		params: GetPullRequestParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequest>> {
		return this.currying(getPullRequest)(params, context);
	}

	updatePullRequest(
		params: UpdatePullRequestParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequest>> {
		return this.currying(updatePullRequest)(params, context);
	}

	getPullRequestComment(
		params: GetPullRequestCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestComment>> {
		return this.currying(getPullRequestComment)(params, context);
	}

	addPullRequestComment(
		params: AddPullRequestCommentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestComment>> {
		return this.currying(addPullRequestComment)(params, context);
	}

	getNumberOfPullRequestComments(
		params: GetNumberOfPullRequestCommentsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestCommentCount>> {
		return this.currying(getNumberOfPullRequestComments)(params, context);
	}

	updatePullRequestCommentInformation(
		params: UpdatePullRequestCommentInformationParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestComment>> {
		return this.currying(updatePullRequestCommentInformation)(params, context);
	}

	getListOfPullRequestAttachment(
		params: GetListOfPullRequestAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestAttachmentFile[]>> {
		return this.currying(getListOfPullRequestAttachment)(params, context);
	}

	downloadPullRequestAttachment(
		params: DownloadPullRequestAttachmentParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(downloadPullRequestAttachment)(params, context);
	}

	deletePullRequestAttachments(
		params: DeletePullRequestAttachmentsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<PullRequestAttachmentFile>> {
		return this.currying(deletePullRequestAttachments)(params, context);
	}

	getWatchingList(
		params: GetWatchingListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItem[]>> {
		return this.currying(getWatchingList)(params, context);
	}

	countWatching(
		params: CountWatchingParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItemCount>> {
		return this.currying(countWatching)(params, context);
	}

	getWatching(
		params: GetWatchingParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItem>> {
		return this.currying(getWatching)(params, context);
	}

	addWatching(
		params: AddWatchingParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItem>> {
		return this.currying(addWatching)(params, context);
	}

	updateWatching(
		params: UpdateWatchingParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItem>> {
		return this.currying(updateWatching)(params, context);
	}

	deleteWatching(
		params: DeleteWatchingParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<WatchingItem>> {
		return this.currying(deleteWatching)(params, context);
	}

	markWatchingAsRead(
		params: MarkWatchingAsReadParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseNoContent>> {
		return this.currying(markWatchingAsRead)(params, context);
	}

	/** @deprecated */
	getProjectGroupList(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getProjectGroupList)(params, context);
	}

	/** @deprecated */
	addProjectGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addProjectGroup)(params, context);
	}

	/** @deprecated */
	deleteProjectGroup(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteProjectGroup)(params, context);
	}

	/** @deprecated */
	getGroupIcon(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(getGroupIcon)(params, context);
	}

	getLicence(context?: BacklogApiContext): Promise<BacklogResponse<Licence>> {
		return this.currying(getLicence)(context);
	}

	getListOfTeams(
		params?: GetListOfTeamsParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Team[]>> {
		return this.currying(getListOfTeams)(params, context);
	}

	addTeam(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(addTeam)(params, context);
	}

	getTeam(
		params: GetTeamParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Team>> {
		return this.currying(getTeam)(params, context);
	}

	updateTeam(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(updateTeam)(params, context);
	}

	deleteTeam(
		params: EmptyObject,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<EmptyObject>> {
		return this.currying(deleteTeam)(params, context);
	}

	getTeamIcon(
		params: GetTeamIconParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		return this.currying(getTeamIcon)(params, context);
	}

	getProjectTeamList(
		params: GetProjectTeamListParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Team[]>> {
		return this.currying(getProjectTeamList)(params, context);
	}

	addProjectTeam(
		params: AddProjectTeamParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Team>> {
		return this.currying(addProjectTeam)(params, context);
	}

	deleteProjectTeam(
		params: DeleteProjectTeamParams,
		context?: BacklogApiContext,
	): Promise<BacklogResponse<Team>> {
		return this.currying(deleteProjectTeam)(params, context);
	}

	getRateLimit(
		context?: BacklogApiContext,
	): Promise<BacklogResponse<RateLimit>> {
		return this.currying(getRateLimit)(context);
	}

	private currying<
		T extends (
			config: BacklogConfig,
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			...args: any[]
		) => ReturnType<T>,
	>(func: T) {
		return (...args: TailType<Parameters<T>>) => func(this.config, ...args);
	}
}
